import {useCallback, useEffect, useMemo, useState} from 'react';

import {useApolloClient} from '@apollo/react-hooks';

import type {MarketId, ProductId} from 'nutshell-core/types';

import type {
    GetProductsByIds as GetProductsByIdsQuery,
    GetProductsByIdsVariables,
} from 'nutshell-graphql-types';

import {enqueueProductHydration} from '../helpers/product-hydration-batcher';
import {toProductPickerProduct} from '../helpers/product-picker-product';
import type {ProductPickerProduct} from '../helpers/product-picker-product';
import GET_PRODUCTS_BY_IDS from '../queries/get-products-by-ids.graphql';

type ProductHydrationState = {
    requestKey: string;
    products: ProductPickerProduct[];
    isLoading: boolean;
    error: unknown | null;
};

const EMPTY_HYDRATION_STATE: ProductHydrationState = {
    requestKey: '',
    products: [],
    isLoading: false,
    error: null,
};

export function useGetProductsByIds(
    productIds: ProductId[],
    marketId?: MarketId
): {
    products: ProductPickerProduct[];
    isLoading: boolean;
    error: unknown | null;
    retry: () => void;
} {
    const client = useApolloClient();
    const productIdsKey = JSON.stringify(Array.from(new Set(productIds)));
    const stableProductIds = useMemo(
        () => JSON.parse(productIdsKey) as ProductId[],
        [productIdsKey]
    );
    const requestKey = `${marketId ?? 'no-market'}:${productIdsKey}`;
    const [attempt, setAttempt] = useState<number>(0);
    const [state, setState] = useState<ProductHydrationState>(EMPTY_HYDRATION_STATE);

    useEffect(() => {
        if (stableProductIds.length === 0) {
            setState({...EMPTY_HYDRATION_STATE, requestKey});
            return;
        }

        let isCurrent = true;
        setState({requestKey, products: [], isLoading: true, error: null});

        enqueueProductHydration(client, stableProductIds, marketId, async (ids) => {
            const {data} = await client.query<GetProductsByIdsQuery, GetProductsByIdsVariables>({
                query: GET_PRODUCTS_BY_IDS,
                variables: {
                    ids,
                    ...(marketId ? {marketId} : {}),
                },
                fetchPolicy: 'no-cache',
            });

            return data.productsByIds.map((result) => toProductPickerProduct(result, marketId));
        })
            .then((products) => {
                if (isCurrent) setState({requestKey, products, isLoading: false, error: null});
            })
            .catch((error: unknown) => {
                if (isCurrent) setState({requestKey, products: [], isLoading: false, error});
            });

        return () => {
            isCurrent = false;
        };
    }, [attempt, client, marketId, productIdsKey, requestKey, stableProductIds]);

    const isCurrentRequest = state.requestKey === requestKey;

    return {
        products: isCurrentRequest ? state.products : [],
        isLoading: stableProductIds.length > 0 && (!isCurrentRequest || state.isLoading),
        error: isCurrentRequest ? state.error : null,
        retry: useCallback(() => setAttempt((value) => value + 1), []),
    };
}
